import { fetchProducts, checkPromoReferralCode, fetchSimulationParameters } from 'api/customerFlow'
import { BannerTypes } from 'components/banner/types'
import { SessionStorageKeys } from 'constants/constants'
import { API_TOKEN, API_URL } from 'constants/envs'
import { ELProduct, NGProduct } from 'features/simulation/types'
import { store } from 'store'
import { getProducts, getProductsError, getProductsSuccess } from 'store/app/actions/getProducts'
import { updateOpportunityId } from 'store/app/actions/updateOpportunityId'
import { updateMarketingData } from 'store/marketing/actions/updateMarketingData'
import { MarketingDataKeys, MarketingStore, ProducerVisual, SignChannel, SimulationType } from 'store/marketing/types'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { SimulationInputKeys } from 'store/simulation/types'
import { CookieKeys } from 'types/cookies'
import { createCookie } from './cookies'
import { trackEvent } from './eventTracking'
import { getPriceSimulationWithInputs, addCashbackPromoCodeToStore } from './helpers'

/**
 * Executes all needed tasks for startup
 */
export const startUp = (): void => {
  if (!API_URL) throw new Error('Missing API URL!')
  if (!API_TOKEN) throw new Error('Missing API Token!')

  initStoreSubscription()

  // Get the products
  getProductsFromApi()

  // Check and save all URL params
  checkAndSaveUrlParameters()
}

// USED STARTUP FUNCTIONS

/**
 * Asks the agent ID in a browser prompt
 */
const askAgentID = (): string => {
  let agentID

  do {
    agentID = prompt('What is your agent ID?')
  } while (!agentID)

  return agentID
}

/**
 * Asks to confirm the the given agent ID in a browser confirm
 *
 * @param agentID
 */
const confirmAgentID = (agentID: string): boolean => {
  return window.confirm(`Do you confirm that your agent ID is "${agentID}"?`)
}

/**
 * Checks and saves all the url paramaters if necessary
 */
const checkAndSaveUrlParameters = async (): Promise<void> => {
  // Fetch the URL params
  const search = new URLSearchParams(window.location.search)

  // CHECK ALL NEEDED PARAMS (alphabetically ordered)

  if (search.has('ask_agent') && search.get('ask_agent') === '1' && !search.has('agent_id')) {
    let agentID
    let confirmed = false

    do {
      agentID = askAgentID()
      confirmed = confirmAgentID(agentID)
    } while (!confirmed)

    store.dispatch(updateMarketingData({ key: MarketingDataKeys.AGENT_ID, value: agentID }))
  }

  if (search.has('adjustable_product'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.ADJUSTABLE_PRODUCT, value: search.get('adjustable_product') === 'true' }))

  if (search.has('agent_id')) store.dispatch(updateMarketingData({ key: MarketingDataKeys.AGENT_ID, value: search.get('agent_id') || '' }))

  if (search.has('banner')) store.dispatch(updateMarketingData({ key: MarketingDataKeys.BANNER, value: search.get('banner') || '' }))

  if (search.has('campaign_name'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.CAMPAIGN_NAME, value: search.get('campaign_name') || '' }))

  if (search.has('ean_code_mandatory'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.EAN_CODE_MANDATORY, value: search.get('ean_code_mandatory') === 'true' }))

  if (search.has('event_name'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.EVENT_NAME, value: search.get('event_name') || '' }))

  if (search.has('event_id')) store.dispatch(updateMarketingData({ key: MarketingDataKeys.EVENT_ID, value: search.get('event_id') || '' }))

  if (search.has('electricityproduct')) {
    switch (search.get('electricityproduct')) {
      case 'bolt':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_ELECTRICITY_PRODUCT, value: ELProduct.VariableOff }))
        break
      case 'online':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_ELECTRICITY_PRODUCT, value: ELProduct.VariableOn }))
        break
      case 'go':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_ELECTRICITY_PRODUCT, value: ELProduct.Go }))
        break
      default:
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_ELECTRICITY_PRODUCT, value: ELProduct.VariableOn }))
        break
    }
  }

  if (search.has('gasproduct')) {
    switch (search.get('gasproduct')) {
      case 'bolt':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_GAS_PRODUCT, value: NGProduct.VariableOff }))
        break
      case 'online':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_GAS_PRODUCT, value: NGProduct.VariableOn }))
        break
      case 'go':
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_GAS_PRODUCT, value: NGProduct.Go }))
        break
      default:
        store.dispatch(updateMarketingData({ key: MarketingDataKeys.DEFAULT_GAS_PRODUCT, value: NGProduct.VariableOn }))
        break
    }
  }

  if (search.has('gclid') && search.get('gclid') !== '') {
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.GCLID, value: search.get('gclid') }))
    createCookie(CookieKeys.GCLID, search.get('gclid'), 90)
  }

  if (search.has('noeanflow'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.NO_EAN_FLOW, value: search.get('noeanflow') === 'yes' }))

  if (search.has('no_optin_one'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.NO_OPTIN_ONE, value: search.get('no_optin_one') === 'true' }))

  if (search.has('nurture_flow'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.NURTURE_FLOW, value: search.get('nurture_flow') || '' }))

  if (search.has('opportunityId') && search.get('opportunityId') !== '') {
    const opportunityId = search.get('opportunityId')

    store.dispatch(updateOpportunityId({ opportunityId }))

    // Should always be executed after opportunityId is stored as we need the opportunityId to fetch the simulation params from the API
    if (search.has('fetchSimulation') && search.get('fetchSimulation') === 'true') {
      const simulationParams = await fetchSimulationParameters(opportunityId)

      if (simulationParams) getPriceSimulationWithInputs(simulationParams)
    }
  }

  if (search.has('partner_id'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.PARTNER_ID, value: search.get('partner_id') || '' }))

  if (search.has('producer_visual'))
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.PRODUCER_VISUAL,
        value: (search.get('producer_visual') as ProducerVisual) || ProducerVisual.THREE_CHOICES
      })
    )

  if (search.has('promocode')) {
    const referralCodeFromQueryString = String(search.get('promocode'))

    try {
      const checkedPromoCode = await checkPromoReferralCode(referralCodeFromQueryString)

      if (checkedPromoCode.valid) {
        const { promoCode } = checkedPromoCode

        store.dispatch(
          updateSimulationInput({
            key: SimulationInputKeys.PROMO_CODE,
            value: promoCode
          })
        )
      }
    } catch (e) {
      throw new Error(e)
    }
  }

  if (search.has('referralcode')) {
    const referralCodeFromQueryString = String(search.get('referralcode'))

    try {
      const checkedReferralCode = await checkPromoReferralCode(referralCodeFromQueryString)

      if (checkedReferralCode.valid) {
        const { referralCode } = checkedReferralCode

        store.dispatch(
          updateSimulationInput({
            key: SimulationInputKeys.REFERRAL_CODE,
            value: referralCode
          })
        )

        // !! We also need to set the cashback promocode (= VIAVIA) because of the referral campaign !!
        addCashbackPromoCodeToStore()
      }
    } catch (e) {
      throw new Error(e)
    }
  }

  if (search.has('reset_button'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.RESET_BUTTON, value: search.get('reset_button') === 'true' }))

  if (search.has('shift_id')) store.dispatch(updateMarketingData({ key: MarketingDataKeys.SHIFT_ID, value: search.get('shift_id') || '' }))

  if (search.has('sign_channel'))
    store.dispatch(
      updateMarketingData({ key: MarketingDataKeys.SIGN_CHANNEL, value: (search.get('sign_channel') as SignChannel) || SignChannel.NONE })
    )

  if (search.has('simulation') && search.get('simulation') === '1') {
    store.dispatch(showSimulation())

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started')
  }

  if (search.has('simulation_input')) getPriceSimulationWithInputs(search.get('simulation_input'))

  if (search.has('simulation_type'))
    store.dispatch(
      updateMarketingData({
        key: MarketingDataKeys.SIMULATION_TYPE,
        value: (search.get('simulation_type') as SimulationType) || SimulationType.PRICING_SIMULATION
      })
    )

  if (search.has('simulation_so')) {
    const simulationSo = search.get('simulation_so') ?? ''
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: simulationSo }))

    // Set BBP banner if simulation_so url param equals the BBP string
    if (simulationSo === 'Brussels Beer Project')
      store.dispatch(updateMarketingData({ key: MarketingDataKeys.BANNER_TYPE, value: BannerTypes.BRUSSELS_BEER_PROJECT }))
  }

  if (search.has('source_type'))
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.SOURCE_TYPE, value: search.get('source_type') || '' }))

  if (search.has('subscribed') && search.get('subscribed') === '1') createCookie(CookieKeys.SUBSCRIBED, 'true')

  if (search.has('trigger_events') && search.get('trigger_events') === '0')
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.TRIGGER_EVENTS, value: search.get('trigger_events') || '' }))

  if (search.has('unlock') && search.get('unlock')) createCookie(CookieKeys.UNLOCK, search.get('unlock') || '')
}

/**
 * Returns the MarketingStore object from the session storage if it was found & could be parsed
 *
 * @returns MarketingStore|undefined
 */
export const fetchMarketingStoreFromSessionStorage = (): MarketingStore | undefined => {
  if (typeof window !== 'undefined') {
    if (window.sessionStorage) {
      const serializedStore = window.sessionStorage.getItem(SessionStorageKeys.MARKETING)

      if (serializedStore) {
        try {
          return JSON.parse(serializedStore)
        } catch (error) {
          window.sessionStorage.removeItem(SessionStorageKeys.MARKETING)
        }
      }
    }
  }
}

/**
 * Gets the pricelists from the API and saves them in the app store
 */
const getProductsFromApi = async (): Promise<void> => {
  try {
    store.dispatch(getProducts())

    const products = await fetchProducts()

    if (products !== null) {
      store.dispatch(getProductsSuccess({ products }))
    }
  } catch {
    store.dispatch(getProductsError())
  }
}

/**
 * Initializes the store subscription (executes given subscribe function everytime the store changes)
 * Saves marketing store to window session storage
 */
const initStoreSubscription = (): void => {
  store.subscribe(() => {
    // Check if window & session storage are defined
    if (window && window.sessionStorage) {
      // Fetch the auth store & save it to the session storage
      const { marketing } = store.getState()
      window.sessionStorage.setItem(SessionStorageKeys.MARKETING, JSON.stringify(marketing))
    }
  })
}
